"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useReducedMotion } from "motion/react";
import { cn } from "@marketing-sites/utils/cn";
import { anchorScroll } from "@marketing-sites/utils/anchor-scroll";
import { cmsAnalyticsId } from "@marketing-sites/analytics/cms-analytics-id";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";

import { Link } from "../../primitives";
import type { BlogTocItem } from "./types";

export type BlogArticleTocMetadata = { analyticsId: string };

function useActiveHeading(ids: string[]) {
  const [activeId, setActiveId] = useState(ids[0]);

  useEffect(() => {
    const headings = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    // The observer callback only reports headings whose visibility *changed*,
    // so track the full visible set across callbacks and pick the first one
    // in document order. When nothing is in the band, keep the last active.
    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const next = ids.find((id) => visible.has(id));
        if (next) setActiveId(next);
      },
      { rootMargin: "0px 0px -70% 0px" },
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [ids]);

  return activeId;
}

// --- Share buttons ---

// Share via LinkedIn post
function linkedInShareUrl(pageUrl: string) {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
}

// Share via X (Twitter)
function xShareUrl(pageUrl: string, title: string) {
  return `https://x.com/intent/post?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(title)}`;
}

// Share via email — title as subject, URL in the body.
function emailShareUrl(pageUrl: string, title: string) {
  return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(pageUrl)}`;
}

// Share icons reproduced from the Blueprint Hub Figma (node 543:15035) as one
// filled monochrome family (`fill="currentColor"`) so they read consistently.
// The old build mixed filled brand glyphs (LinkedIn/Facebook) with Heroicons
// outline strokes (X/link), and the link icon used a 24px path inside a
// `0 0 20 20` viewBox — hence the broken overflow. Each icon keeps its Figma
// source viewBox; sizes mirror the Figma's per-glyph boxing in the 24px button.

// Material Symbols "link" — fills the 20px icon box.
function LinkIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M12.2803 13.7939L10.088 15.9862C9.32982 16.7444 8.41636 17.1235 7.34762 17.1235C6.27887 17.1235 5.36541 16.7444 4.60724 15.9862C3.84907 15.2281 3.46998 14.3146 3.46998 13.2458C3.46998 12.1771 3.84907 11.2636 4.60724 10.5055L6.79954 8.31317L7.89569 9.40932L5.70339 11.6016C5.24666 12.0584 5.0183 12.6064 5.0183 13.2458C5.0183 13.8853 5.24666 14.4333 5.70339 14.8901C6.16012 15.3468 6.70819 15.5752 7.34762 15.5752C7.98704 15.5752 8.53511 15.3468 8.99184 14.8901L11.1841 12.6978L12.2803 13.7939ZM8.44377 13.2458L7.34762 12.1497L11.7322 7.7651L12.8284 8.86125L8.44377 13.2458ZM13.3764 12.6978L12.2803 11.6016L14.4726 9.40932C14.9293 8.95259 15.1577 8.40452 15.1577 7.7651C15.1577 7.12567 14.9293 6.5776 14.4726 6.12087C14.0159 5.66414 13.4678 5.43578 12.8284 5.43578C12.1889 5.43578 11.6409 5.66414 11.1841 6.12087L8.99184 8.31317L7.89569 7.21702L10.088 5.02472C10.8462 4.26655 11.7596 3.88746 12.8284 3.88746C13.8971 3.88746 14.8106 4.26655 15.5687 5.02472C16.3269 5.78289 16.706 6.69635 16.706 7.7651C16.706 8.83384 16.3269 9.7473 15.5687 10.5055L13.3764 12.6978Z" />
    </svg>
  );
}

// Checkmark shown transiently after copying — stroke glyph is only visible for
// ~2s, so it doesn't break the filled family at rest.
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      className="size-4"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 10.5 8 14l7.5-7.5"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg
      viewBox="0 0 14.4444 14.4444"
      fill="currentColor"
      className="size-3.5"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.4444 14.4444H11.4766V9.38948C11.4766 8.00354 10.9499 7.22904 9.85297 7.22904C8.65961 7.22904 8.03613 8.03503 8.03613 9.38948V14.4444H5.17593V4.81482H8.03613V6.11193C8.03613 6.11193 8.89612 4.52062 10.9396 4.52062C12.9821 4.52062 14.4444 5.76791 14.4444 8.34756V14.4444ZM1.76371 3.55389C0.78946 3.55389 0 2.75824 0 1.77695C0 0.795655 0.78946 0 1.76371 0C2.73796 0 3.52695 0.795655 3.52695 1.77695C3.52695 2.75824 2.73796 3.55389 1.76371 3.55389ZM0.28682 14.4444H3.26928V4.81482H0.28682V14.4444Z"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 15 15"
      fill="currentColor"
      className="size-[15px]"
      aria-hidden
    >
      <path d="M13.2676 13.8965C13.2219 13.9782 13.1548 14.0463 13.0731 14.0939C12.9915 14.1414 12.8984 14.1665 12.8035 14.1667H9.62165C9.5324 14.1666 9.4446 14.1445 9.36638 14.1023C9.28816 14.06 9.22206 13.9991 9.1742 13.9251L6.49014 9.78255L2.58967 13.9961C2.49459 14.0964 2.36308 14.1559 2.22365 14.1616C2.08423 14.1673 1.94813 14.1188 1.84485 14.0267C1.74158 13.9345 1.67946 13.806 1.67197 13.6692C1.66447 13.5323 1.7122 13.3981 1.80481 13.2956L5.8995 8.86849L1.74979 2.46745C1.69868 2.3887 1.67007 2.29791 1.66695 2.20457C1.66384 2.11124 1.68633 2.0188 1.73207 1.93693C1.77782 1.85506 1.84513 1.78678 1.92697 1.73923C2.00881 1.69168 2.10215 1.66662 2.19724 1.66667H5.37913C5.46838 1.66669 5.55619 1.68884 5.6344 1.73107C5.71262 1.77329 5.77872 1.83422 5.82658 1.9082L8.51064 6.05078L12.4111 1.83724C12.5062 1.73693 12.6377 1.67746 12.7771 1.67174C12.9166 1.66602 13.0527 1.7145 13.1559 1.80668C13.2592 1.89885 13.3213 2.02729 13.3288 2.16414C13.3363 2.301 13.2886 2.43524 13.196 2.53776L9.10128 6.96159L13.251 13.3665C13.3018 13.4453 13.3302 13.5361 13.3331 13.6293C13.336 13.7225 13.3134 13.8148 13.2676 13.8965Z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg
      viewBox="0 0 15 15"
      fill="currentColor"
      className="size-[15px]"
      aria-hidden
    >
      <path d="M1.875 3.28125C1.61719 3.28125 1.40625 3.49219 1.40625 3.75V4.39746L6.45996 8.5459C7.06641 9.04395 7.93652 9.04395 8.54297 8.5459L13.5937 4.39746V3.75C13.5937 3.49219 13.3828 3.28125 13.125 3.28125H1.875ZM1.40625 6.2168V11.25C1.40625 11.5078 1.61719 11.7187 1.875 11.7187H13.125C13.3828 11.7187 13.5937 11.5078 13.5937 11.25V6.2168L9.43359 9.63281C8.30859 10.5557 6.68848 10.5557 5.56641 9.63281L1.40625 6.2168ZM0 3.75C0 2.71582 0.84082 1.875 1.875 1.875H13.125C14.1592 1.875 15 2.71582 15 3.75V11.25C15 12.2842 14.1592 13.125 13.125 13.125H1.875C0.84082 13.125 0 12.2842 0 11.25V3.75Z" />
    </svg>
  );
}

// The canonical URL comes from the server, so the row renders identically on
// both sides — no hydration placeholder, and no visitor query string shared.
function ShareButtons({
  pageUrl,
  pageTitle,
  copyLinkMetadata,
  linkedInShareMetadata,
  xShareMetadata,
  emailShareMetadata,
}: {
  pageUrl: string;
  pageTitle: string;
  copyLinkMetadata: BlogArticleTocMetadata;
  linkedInShareMetadata: BlogArticleTocMetadata;
  xShareMetadata: BlogArticleTocMetadata;
  emailShareMetadata: BlogArticleTocMetadata;
}) {
  const [copied, setCopied] = useState(false);
  // Hold the "copied" reset timer so we can clear it on unmount — a cleanup
  // returned from inside a `.then()` callback is ignored (it isn't a
  // useEffect cleanup), so we track the id in a ref instead.
  const copiedResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedResetTimer.current) {
        clearTimeout(copiedResetTimer.current);
      }
    };
  }, []);

  const handleCopyLink = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      triggerAnalyticsClick({
        text: "Copy link",
        location: getAnalyticsRegion(event.currentTarget) ?? "Unknown",
        analyticsId: copyLinkMetadata.analyticsId,
      });
      navigator.clipboard.writeText(pageUrl).then(() => {
        setCopied(true);
        if (copiedResetTimer.current) clearTimeout(copiedResetTimer.current);
        copiedResetTimer.current = setTimeout(() => setCopied(false), 2000);
      });
    },
    [pageUrl, copyLinkMetadata.analyticsId],
  );

  // The border derives from the rail's own text colour via `currentColor`.
  const buttonClass =
    "flex size-6 items-center justify-center rounded-[4px] border border-current text-current transition-colors hover:bg-current/10 focus-visible:outline-2 focus-visible:outline-offset-2";

  return (
    <div className="mt-3 flex gap-3">
      <button
        type="button"
        onClick={handleCopyLink}
        className={cn(buttonClass, "cursor-pointer")}
        aria-label={copied ? "Link copied" : "Copy link"}
      >
        {copied ? <CheckIcon /> : <LinkIcon />}
      </button>
      <Link
        href={linkedInShareUrl(pageUrl)}
        target="_blank"
        rel="noreferrer"
        className={buttonClass}
        aria-label="Share on LinkedIn"
        analyticsId={linkedInShareMetadata.analyticsId}
        analyticsLabel="LinkedIn"
      >
        <LinkedInIcon />
      </Link>
      <Link
        href={xShareUrl(pageUrl, pageTitle)}
        target="_blank"
        rel="noreferrer"
        className={buttonClass}
        aria-label="Share on X (Twitter)"
        analyticsId={xShareMetadata.analyticsId}
        analyticsLabel="X (Twitter)"
      >
        <XIcon />
      </Link>
      <Link
        href={emailShareUrl(pageUrl, pageTitle)}
        className={buttonClass}
        aria-label="Share by email"
        analyticsId={emailShareMetadata.analyticsId}
        analyticsLabel="Email"
      >
        <EmailIcon />
      </Link>
    </div>
  );
}

export type BlogArticleTocProps = {
  items: BlogTocItem[];
  /** Canonical absolute URL, used by the share buttons. */
  pageUrl: string;
  pageTitle: string;
  sectionMetadata: BlogArticleTocMetadata;
  contentsLinkMetadata: BlogArticleTocMetadata;
  copyLinkMetadata: BlogArticleTocMetadata;
  linkedInShareMetadata: BlogArticleTocMetadata;
  xShareMetadata: BlogArticleTocMetadata;
  emailShareMetadata: BlogArticleTocMetadata;
  className?: string;
};

export default function BlogArticleToc({
  items,
  pageUrl,
  pageTitle,
  sectionMetadata,
  contentsLinkMetadata,
  copyLinkMetadata,
  linkedInShareMetadata,
  xShareMetadata,
  emailShareMetadata,
  className,
}: BlogArticleTocProps) {
  const ids = useMemo(
    () => items.map((item) => item.href.replace(/^#/, "")),
    [items],
  );
  const activeId = useActiveHeading(ids);
  const prefersReducedMotion = useReducedMotion();

  const handleTocClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (!document.getElementById(href.replace(/^#/, ""))) return;

      event.preventDefault();
      anchorScroll(href, {
        isReducedMotion: !!prefersReducedMotion,
        // Only push a history entry when the hash actually changes — re-clicking
        // the current section shouldn't stack a duplicate Back step.
        updateUrl: href !== window.location.hash,
      });
    },
    [prefersReducedMotion],
  );

  const hasContents = items.length > 0;
  const canShare = pageUrl !== "";
  if (!hasContents && !canShare) return null;

  return (
    <nav
      aria-label={hasContents ? "Table of contents" : "Share article"}
      data-analytics-region="table-of-contents"
      data-analytics-id={sectionMetadata.analyticsId}
      className={cn(
        // Figma 176:7703: 233px rail width, no matching token.
        "lg:w-[233px]",
        "gap-13 flex flex-col self-start lg:sticky lg:top-24",
        "lg:max-h-[calc(100svh-6rem)] lg:overflow-y-auto",
        "text-off-black",
        className,
      )}
    >
      {hasContents && (
        <div className="flex flex-col gap-y-2.5">
          <p className="text-label-lg">Contents</p>
          <ul className="flex flex-col gap-[9px]">
            {items.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  onClick={(event) => handleTocClick(event, item.href)}
                  aria-current={
                    item.href === `#${activeId}` ? "location" : undefined
                  }
                  className="text-body text-current/60 block hover:text-current focus:text-current aria-[current=location]:text-current"
                  analyticsId={cmsAnalyticsId(
                    item._key,
                    contentsLinkMetadata.analyticsId,
                  )}
                  analyticsLabel={item.label}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canShare && (
        <div>
          <p className="text-label-lg">Share article</p>
          <ShareButtons
            pageUrl={pageUrl}
            pageTitle={pageTitle}
            copyLinkMetadata={copyLinkMetadata}
            linkedInShareMetadata={linkedInShareMetadata}
            xShareMetadata={xShareMetadata}
            emailShareMetadata={emailShareMetadata}
          />
        </div>
      )}
    </nav>
  );
}
